import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { PlayerRollup } from './playerHealthFormat';
import { ROLLUP_COLORS, rollupSummary } from './playerHealthFormat';

interface PlayerStatusDotProps {
  rollup: PlayerRollup | undefined;
  /** Filter to apply on the Player Health page when the dot is clicked. */
  filter: { organization_id?: string; site_id?: string; zone_id?: string };
  size?: number;
}

/**
 * Rolled-up player status for an organization, site or zone row.
 * Red = a player is dead, amber = a player is stale, green = all alive,
 * grey = nothing has reported from here. Click opens Player Health
 * filtered to the row.
 */
export default function PlayerStatusDot({ rollup, filter, size = 10 }: PlayerStatusDotProps) {
  const navigate = useNavigate();
  const status = rollup?.status || 'none';
  const color = ROLLUP_COLORS[status];
  const params = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  const href = `/fleet/player-health${params.toString() ? `?${params.toString()}` : ''}`;

  return (
    <Tooltip title={`${rollupSummary(rollup)}. Click for Player Health.`}>
      <Box
        component="span"
        role="link"
        aria-label={rollupSummary(rollup)}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          navigate(href);
        }}
        sx={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: color,
          boxShadow: status === 'none' ? 'none' : `0 0 0 3px ${color}30`,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
}
